import { Card, CardContent } from '@/components/ui/card';
import type { DrawdownMatrixRow } from '@/types/drawdown';
import { formatMatrixAmount } from './drawdownMatrixAmount';
import { totalRows } from './drawdownMatrixTotals';

interface DrawdownMatrixSummaryProps {
  /** Every investor of the fund: the tiles report the fund, not the filters */
  rows: DrawdownMatrixRow[];
  committed: number;
  currency: string;
  drawdownCount: number;
}

export function DrawdownMatrixSummary({
  rows,
  committed,
  currency,
  drawdownCount,
}: DrawdownMatrixSummaryProps) {
  const totals = totalRows(rows);
  const calledOfCommitted =
    committed > 0 ? (totals.amountDue / committed) * 100 : 0;
  const collected =
    totals.amountDue > 0 ? (totals.amountPaid / totals.amountDue) * 100 : 0;
  const money = (amount: number) => formatMatrixAmount(amount, currency);

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <Card>
        <CardContent className="p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            Called to date
          </p>
          <p className="text-2xl font-semibold tabular-nums">
            {money(totals.amountDue)}
          </p>
          <p className="text-xs text-muted-foreground">
            {calledOfCommitted.toFixed(1)}% of {money(committed)} committed ·{' '}
            {drawdownCount} {drawdownCount === 1 ? 'call' : 'calls'}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            Received
          </p>
          <p className="text-2xl font-semibold tabular-nums">
            {money(totals.amountPaid)}
          </p>
          <p className="text-xs text-muted-foreground">
            {collected.toFixed(1)}% of called
          </p>
          <span className="mt-2 block h-1 w-full overflow-hidden rounded-full bg-muted">
            <span
              className="block h-full rounded-full bg-green-600"
              style={{ width: `${Math.min(collected, 100)}%` }}
            />
          </span>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            Outstanding
          </p>
          <p className="text-2xl font-semibold tabular-nums">
            {money(totals.outstanding)}
          </p>
        </CardContent>
      </Card>

      <Card className="border-l-4 border-l-red-600">
        <CardContent className="p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            Overdue
          </p>
          <p className="text-2xl font-semibold tabular-nums text-red-700 dark:text-red-400">
            {money(totals.overdue)}
          </p>
          <p className="text-xs text-muted-foreground">
            {totals.overdueInvestorCount}{' '}
            {totals.overdueInvestorCount === 1 ? 'investor' : 'investors'}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
